/**
 * Portal del Empleado — Fase B activada:
 *   - Tab Calendario: vista mensual con festivos, vacaciones aprobadas y pendientes.
 *   - Tab Mis vacaciones: lista de solicitudes + botón "Solicitar vacaciones".
 *   - Tab Mis nóminas: placeholder (Fase C).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, PlaneTakeoff, FileText, ChevronLeft, ChevronRight, Plus, X, Trash2, Download } from "lucide-react";
import VacationRequestModal from "@/components/portal/VacationRequestModal";

const TABS = [
  { id: "calendar",  label: "Calendario",     icon: CalendarDays },
  { id: "vacations", label: "Mis vacaciones", icon: PlaneTakeoff },
  { id: "payroll",   label: "Mis nóminas",    icon: FileText },
];

const MONTHS = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
const DOW = ["L","M","X","J","V","S","D"];

export default function EmployeePortal() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("calendar");
  const [error, setError] = useState("");

  const reload = () => {
    api.get("/portal/me/summary")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error cargando el portal"));
  };
  useEffect(() => { reload(); }, []);

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="ds-card p-6 text-sm text-red-800 bg-red-50 border-red-200">{error}</div>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-sm text-[#8A9098]">Cargando portal...</div>;

  const v = data.vacation || {};

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-6 lg:py-10" data-testid="employee-portal">
      <header className="pb-6 border-b border-dashed border-[#D5D7CE]">
        <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
          <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Mi portal
        </div>
        <h1 className="font-display text-4xl lg:text-[42px] font-black mt-2 tracking-tight" data-testid="portal-greeting">
          Hola, {(data.employee?.name || "").split(" ")[0] || "empleado"}
        </h1>
        <p className="text-sm text-[#4B5158] mt-1">Resumen de tu año laboral · {data.year}</p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="portal-summary">
        <SummaryCard label="Anuales"     value={v.annual}    testid="s-annual" />
        <SummaryCard label="Disfrutados" value={v.approved}  tone="violet" testid="s-approved" />
        <SummaryCard label="Pendientes"  value={v.pending}   tone="amber"  testid="s-pending" />
        <SummaryCard label="Disponibles" value={v.available} tone="brand"  strong testid="s-available" />
      </section>

      <nav className="mt-8 flex gap-1 border-b border-[#E6E7E1]">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`portal-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
              tab === t.id ? "text-[#0F1210] border-[#6E8B3D]" : "text-[#8A9098] border-transparent hover:text-[#4B5158]"
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "calendar" && <CalendarTab year={data.year} />}
        {tab === "vacations" && <VacationsTab onChange={reload} />}
        {tab === "payroll" && <PayrollTab />}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone, strong, testid }) {
  const toneStyle = {
    brand:  { bg: "linear-gradient(160deg,#EFF3E6,#E4EDD3)", ink: "#3F5222" },
    amber:  { bg: "linear-gradient(160deg,#FFF7E6,#FCEBC7)", ink: "#8C5A00" },
    violet: { bg: "linear-gradient(160deg,#F1EBFB,#E4D9F5)", ink: "#4E2E86" },
  }[tone] || { bg: "linear-gradient(160deg,#FDFDFB,#F5F6F2)", ink: "#0F1210" };
  return (
    <div className="ds-card p-5 ds-lift" style={{ background: toneStyle.bg, borderColor: "transparent" }} data-testid={testid}>
      <div className="font-mono text-[10.5px] uppercase tracking-[.14em] font-semibold" style={{ color: toneStyle.ink, opacity: .8 }}>{label}</div>
      <div className="font-display font-black leading-none tracking-tight mt-2"
           style={{ color: toneStyle.ink, fontSize: strong ? 40 : 34 }}>{value ?? 0}</div>
      <div className="text-[11px] mt-2" style={{ color: toneStyle.ink, opacity: .7 }}>días</div>
    </div>
  );
}

/* ================= CALENDAR TAB ================= */
function CalendarTab({ year }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [y, setY] = useState(year);
  const [cal, setCal] = useState(null);
  useEffect(() => {
    api.get("/portal/me/calendar", { params: { year: y, month } }).then((r) => setCal(r.data)).catch(() => {});
  }, [y, month]);
  const nav = (delta) => {
    let m = month + delta, yy = y;
    if (m < 1)  { m = 12; yy--; }
    if (m > 12) { m = 1;  yy++; }
    setMonth(m); setY(yy);
  };
  const cells = useMemo(() => {
    if (!cal) return [];
    const out = [];
    const pad = (cal.first_weekday + 6) % 7;
    for (let i = 0; i < pad; i++) out.push({ empty: true, key: `p${i}` });
    for (const d of cal.days) out.push({ ...d, key: d.date });
    return out;
  }, [cal]);
  return (
    <div className="ds-card p-5" data-testid="portal-calendar">
      <div className="flex items-center justify-between mb-3">
        <button className="ds-btn" style={{ padding: "6px 10px" }} onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft size={13}/></button>
        <div className="font-display font-black text-lg capitalize">{MONTHS[month-1]} {y}</div>
        <button className="ds-btn" style={{ padding: "6px 10px" }} onClick={() => nav(1)} data-testid="cal-next"><ChevronRight size={13}/></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW.map((d) => <div key={d} className="text-center font-mono text-[10px] uppercase tracking-widest text-[#8A9098] font-semibold py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => c.empty ? <div key={c.key}/> : (
          <div key={c.key}
               title={c.holiday || (c.is_approved_vac ? "Vacaciones aprobadas" : c.is_pending_vac ? "Vacaciones pendientes" : "")}
               data-testid={`portal-cal-${c.date}`}
               className="aspect-square flex flex-col items-center justify-center text-sm rounded-md border transition-colors"
               style={dayStyle(c)}>
            <span className="font-mono">{Number(c.date.slice(8, 10))}</span>
            {c.holiday && <span className="text-[8px] px-[2px] leading-tight truncate max-w-full">{c.holiday.slice(0, 8)}</span>}
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-3 flex-wrap text-[10.5px] text-[#4B5158]">
        <Legend color="#FDEDE7" borderCol="#B4381F" label="Festivo"/>
        <Legend color="#EEF0EA" borderCol="#D5D7CE" label="Fin de semana"/>
        <Legend color="#F1EBFB" borderCol="#6D4AA6" label="Vacaciones aprobadas"/>
        <Legend color="#FFF7E6" borderCol="#D68A00" label="Vacaciones pendientes"/>
      </div>
    </div>
  );
}
function dayStyle(c) {
  let bg = "#FFFFFF", border = "#E6E7E1", color = "#0F1210";
  if (c.holiday)         { bg = "#FDEDE7"; border = "rgba(180,56,31,.28)"; color = "#8C2B14"; }
  else if (c.is_weekend) { bg = "#EEF0EA"; border = "#E0E2DA"; color = "#8A9098"; }
  if (c.is_approved_vac) { bg = "#F1EBFB"; border = "rgba(109,74,166,.35)"; color = "#4E2E86"; }
  if (c.is_pending_vac)  { bg = "#FFF7E6"; border = "rgba(214,138,0,.35)"; color = "#8C5A00"; }
  return { background: bg, borderColor: border, color };
}
function Legend({ color, borderCol, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color, border: `1px solid ${borderCol}` }}/>
      {label}
    </span>
  );
}

/* ================= VACATIONS TAB ================= */
function VacationsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const load = () => api.get("/portal/me/vacation-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta solicitud pendiente?")) return;
    try { await api.delete(`/portal/me/vacation-requests/${id}`); toast.success("Cancelada"); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="portal-vacations">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#4B5158]">{rows.length} solicitud{rows.length !== 1 && "es"}</div>
        <button className="ds-btn primary" onClick={() => setOpenModal(true)} data-testid="portal-vac-new">
          <Plus size={13}/> Solicitar vacaciones
        </button>
      </div>

      {rows.length === 0 && (
        <div className="ds-card p-10 text-center text-[#8A9098] text-sm">Aún no has creado solicitudes.</div>
      )}
      {rows.map((r) => <RequestCard key={r.id} r={r} onCancel={() => cancel(r.id)} />)}

      {openModal && (
        <VacationRequestModal
          onClose={() => setOpenModal(false)}
          onCreated={() => { load(); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const tokens = {
    pending:  { bg: "#FFF7E6", tx: "#8C5A00", dot: "#D68A00", label: "Pendiente" },
    approved: { bg: "#E6F6EE", tx: "#136A34", dot: "#22A05B", label: "Aprobada" },
    rejected: { bg: "#FDEDE7", tx: "#8C2B14", dot: "#B4381F", label: "Rechazada" },
  }[status] || { bg: "#EEF0EA", tx: "#4B5158", dot: "#8A9098", label: status };
  return (
    <span className="ds-pill" style={{ background: tokens.bg, color: tokens.tx }}>
      <span className="dot" style={{ background: tokens.dot }}/>
      {tokens.label}
    </span>
  );
}

function RequestCard({ r, onCancel }) {
  const groups = groupConsecutive(r.billable_days || []);
  return (
    <div className="ds-card p-5" data-testid={`portal-req-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <StatusBadge status={r.status}/>
            <span className="font-display font-black text-lg tracking-tight">{r.billable_count} días</span>
            <span className="text-xs text-[#8A9098]">Solicitada {formatDate(r.created_at)}</span>
          </div>
          {r.comment && <div className="mt-2 text-[12.5px] text-[#4B5158] italic">"{r.comment}"</div>}
          {r.decision_comment && (
            <div className="mt-1 text-[12px] text-[#B4381F]">Motivo rechazo: {r.decision_comment}</div>
          )}
          {r.decided_at && (
            <div className="mt-1 text-[11px] text-[#8A9098]">
              {r.status === "approved" ? "Aprobada" : "Rechazada"} por {r.decided_by} · {formatDate(r.decided_at)}
            </div>
          )}
        </div>
        {r.status === "pending" && (
          <button className="ds-btn danger" style={{ padding: "5px 10px", fontSize: 11 }} onClick={onCancel} data-testid={`portal-req-cancel-${r.id}`}>
            <Trash2 size={11}/> Cancelar
          </button>
        )}
      </div>

      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[12px] font-mono">
        {groups.map((g, i) => (
          <li key={i} className="px-2 py-1 rounded-md bg-[#FBFBF8] border border-[#E6E7E1] flex justify-between">
            <span>{g.start === g.end ? fmt(g.start) : `${fmt(g.start)} → ${fmt(g.end)}`}</span>
            <b className="text-[#3F5222]">{g.count}d</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PayrollTab() {
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    api.get("/portal/me/payrolls").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  const download = async (r) => {
    setDownloading(r.id);
    try {
      const resp = await api.get(`/portal/me/payrolls/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename || `nomina_${r.period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(null); }
  };
  return (
    <div className="space-y-3" data-testid="portal-payroll">
      <div className="text-sm text-[#4B5158]">{rows.length}/3 nóminas más recientes disponibles</div>
      {rows.length === 0 && (
        <div className="ds-card p-10 text-center text-[#8A9098] text-sm">
          Todavía no hay nóminas cargadas para ti.
        </div>
      )}
      {rows.map((r) => (
        <div key={r.id} className="ds-card p-4 flex items-center gap-4" data-testid={`portal-payroll-${r.id}`}>
          <div className="w-10 h-10 rounded-lg grid place-items-center flex-none" style={{ background: "#FDEDE7", color: "#B4381F" }}>
            <FileText size={18}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm capitalize">
              {monthLabel(r.month)} {r.year}
            </div>
            <div className="text-[11px] text-[#8A9098] font-mono truncate">
              {r.original_filename} · {(r.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button className="ds-btn primary" onClick={() => download(r)} disabled={downloading === r.id}
                  data-testid={`portal-payroll-download-${r.id}`}>
            <Download size={13}/> {downloading === r.id ? "Descargando…" : "Descargar"}
          </button>
        </div>
      ))}
    </div>
  );
}

function monthLabel(m) {
  return ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"][m - 1] || "";
}

/* ============= helpers ============= */
function groupConsecutive(datesISO) {
  const arr = [...datesISO].sort();
  const groups = [];
  for (const iso of arr) {
    const last = groups[groups.length - 1];
    if (last) {
      const [y,m,d] = last.end.split("-").map(Number);
      const next = new Date(y, m-1, d + 1);
      const nextISO = next.toISOString().slice(0, 10);
      if (nextISO === iso) { last.end = iso; last.count++; continue; }
    }
    groups.push({ start: iso, end: iso, count: 1 });
  }
  return groups;
}
function fmt(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
}
function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
